import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { 
  GitBranch, Plus, Calendar, TrendingUp, TrendingDown, Minus, 
  User, Clock, AlertCircle, CheckCircle2, Loader2, ChevronRight,
  Play, Trash2, Info, AlertTriangle, ArrowRight, Copy, Flag,
  Users, CloudRain, FastForward, Zap, Settings, RefreshCw,
  FileText, BarChart3, Layers, X
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useProject } from '../contexts/ProjectContext';
import { Action } from '../lib/auth';
import api from '../lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';

// Icon mapping for template types
const TEMPLATE_ICONS = {
  'users-plus': Users,
  'users-minus': Users,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  'calendar-plus': Calendar,
  'clock-plus': Clock,
  'fast-forward': FastForward,
  'cloud-rain': CloudRain,
  'git-branch': GitBranch,
};

// Category colors
const CATEGORY_COLORS = {
  resource: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
  duration: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', badge: 'bg-green-100 text-green-700' },
  calendar: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700' },
  logic: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  risk: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  custom: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-600' },
};

const ScenariosPage = () => {
  const { activeSchedule, can } = useProject();
  
  // State
  const [scenarios, setScenarios] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [error, setError] = useState(null);
  
  // Selected scenario state
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenarioActivities, setScenarioActivities] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createMode, setCreateMode] = useState('template');
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [newScenario, setNewScenario] = useState({ name: '', description: '' });
  const [templateParameters, setTemplateParameters] = useState({});
  
  // Action dialogs
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [baselineName, setBaselineName] = useState('');
  const [baselines, setBaselines] = useState([]);
  const [selectedBaselineId, setSelectedBaselineId] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  
  // Load templates
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await api.get('/scenarios/templates');
      setTemplates(response.data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);
  
  // Load scenarios
  const loadScenarios = useCallback(async () => {
    if (!activeSchedule?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/scenarios/schedule/${activeSchedule.id}`);
      setScenarios(response.data);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
      setError('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  }, [activeSchedule?.id]);
  
  // Load baselines for comparison
  const loadBaselines = useCallback(async () => {
    if (!activeSchedule?.id) return;
    try {
      const response = await api.get(`/baselines/schedule/${activeSchedule.id}`);
      setBaselines(response.data);
    } catch (err) {
      console.error('Failed to load baselines:', err);
    }
  }, [activeSchedule?.id]);
  
  useEffect(() => {
    loadTemplates();
    loadScenarios();
    loadBaselines();
  }, [loadTemplates, loadScenarios, loadBaselines]);
  
  // Initialize template parameters when template is selected
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        const defaults = {};
        template.parameters.forEach(p => {
          defaults[p.key] = p.default;
        });
        setTemplateParameters(defaults);
        setNewScenario(prev => ({
          ...prev,
          name: prev.name || template.name,
          description: prev.description || template.description
        }));
      }
    }
  }, [selectedTemplateId, templates]);
  
  // Load scenario details
  const loadScenarioDetails = async (scenarioId) => {
    setSelectedScenarioId(scenarioId);
    setLoadingDetails(true);
    
    try {
      const [scenarioRes, forecastRes, activitiesRes] = await Promise.all([
        api.get(`/scenarios/${scenarioId}`),
        api.get(`/scenarios/${scenarioId}/forecast`),
        api.get(`/scenarios/${scenarioId}/activities`)
      ]);
      
      setSelectedScenario(scenarioRes.data);
      setForecast(forecastRes.data);
      setScenarioActivities(activitiesRes.data);
    } catch (err) {
      console.error('Failed to load scenario details:', err);
      toast.error('Failed to load scenario details');
    } finally {
      setLoadingDetails(false);
    }
  };
  
  // Create scenario from template
  const handleCreateFromTemplate = async () => {
    if (!activeSchedule?.id || !selectedTemplateId || !newScenario.name.trim()) return;
    
    setCreating(true);
    try {
      const response = await api.post('/scenarios/from-template', {
        schedule_id: activeSchedule.id,
        template_id: selectedTemplateId,
        name: newScenario.name.trim(),
        description: newScenario.description?.trim() || null,
        parameters: templateParameters
      });
      
      toast.success('Scenario created successfully');
      setCreateDialogOpen(false);
      resetCreateForm();
      await loadScenarios();
      loadScenarioDetails(response.data.id);
    } catch (error) {
      console.error('Failed to create scenario:', error);
      toast.error(apiErrorMessage(error, 'Failed to create scenario'));
    } finally {
      setCreating(false);
    }
  };
  
  // Create custom scenario
  const handleCreateCustom = async () => {
    if (!activeSchedule?.id || !newScenario.name.trim()) return;
    
    setCreating(true);
    try {
      const response = await api.post('/scenarios/custom', {
        schedule_id: activeSchedule.id,
        name: newScenario.name.trim(),
        description: newScenario.description?.trim() || null
      });
      
      toast.success('Custom scenario created');
      setCreateDialogOpen(false);
      resetCreateForm();
      await loadScenarios();
      loadScenarioDetails(response.data.id);
    } catch (error) {
      console.error('Failed to create scenario:', error);
      toast.error(apiErrorMessage(error, 'Failed to create scenario'));
    } finally {
      setCreating(false);
    }
  };
  
  // Duplicate scenario
  const handleDuplicate = async () => {
    if (!selectedScenarioId || !duplicateName.trim()) return;
    
    setCreating(true);
    try {
      const response = await api.post(`/scenarios/${selectedScenarioId}/duplicate`, {
        new_name: duplicateName.trim()
      });
      
      toast.success('Scenario duplicated');
      setShowDuplicateDialog(false);
      setDuplicateName('');
      await loadScenarios();
      loadScenarioDetails(response.data.id);
    } catch (error) {
      console.error('Failed to duplicate scenario:', error);
      toast.error('Failed to duplicate scenario');
    } finally {
      setCreating(false);
    }
  };
  
  // Convert to baseline
  const handleConvertToBaseline = async () => {
    if (!selectedScenarioId || !baselineName.trim()) return;
    
    setCreating(true);
    try {
      await api.post(`/scenarios/${selectedScenarioId}/convert-to-baseline`, {
        baseline_name: baselineName.trim(),
        baseline_type: 'what_if'
      });
      
      toast.success('Scenario converted to baseline');
      setShowConvertDialog(false);
      setBaselineName('');
      await loadScenarios();
      await loadBaselines();
      loadScenarioDetails(selectedScenarioId);
    } catch (error) {
      console.error('Failed to convert to baseline:', error);
      toast.error('Failed to convert to baseline');
    } finally {
      setCreating(false);
    }
  };
  
  // Compare with baseline
  const handleCompare = async () => {
    if (!selectedScenarioId || !selectedBaselineId) return;
    
    setLoadingComparison(true);
    try {
      const response = await api.get(`/scenarios/${selectedScenarioId}/compare/${selectedBaselineId}`);
      setComparisonData(response.data);
    } catch (error) {
      console.error('Failed to load comparison:', error);
      toast.error('Failed to load comparison data');
    } finally {
      setLoadingComparison(false);
    }
  };
  
  // Delete scenario
  const handleDeleteScenario = async (scenarioId) => {
    if (!window.confirm('Delete this scenario? This cannot be undone.')) return;
    
    try {
      await api.delete(`/scenarios/${scenarioId}`);
      toast.success('Scenario deleted');
      loadScenarios();
      if (selectedScenarioId === scenarioId) {
        setSelectedScenarioId(null);
        setSelectedScenario(null);
        setForecast(null);
        setScenarioActivities([]);
      }
    } catch (err) {
      console.error('Failed to delete scenario:', err);
      toast.error('Failed to delete scenario');
    }
  };
  
  // Reset create form
  const resetCreateForm = () => {
    setNewScenario({ name: '', description: '' });
    setSelectedTemplateId(null);
    setTemplateParameters({});
    setCreateMode('template');
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };
  
  // Get template icon
  const getTemplateIcon = (iconName) => {
    return TEMPLATE_ICONS[iconName] || GitBranch;
  };
  
  // Get category style
  const getCategoryStyle = (category) => {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS.custom;
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-blue-100 text-blue-700',
      analyzing: 'bg-yellow-100 text-yellow-700',
      saved: 'bg-green-100 text-green-700',
      applied: 'bg-purple-100 text-purple-700',
      discarded: 'bg-slate-100 text-slate-600',
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-600'}>{status}</Badge>;
  };
  
  // Get variance badge
  const getVarianceBadge = (days) => {
    if (days === null || days === undefined) return null;
    if (days > 0) {
      return <Badge className="bg-red-100 text-red-700">+{days}d</Badge>;
    } else if (days < 0) {
      return <Badge className="bg-green-100 text-green-700">{days}d</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600">0d</Badge>;
  };
  
  // Render parameter input
  const renderParameterInput = (param, value, onChange) => {
    switch (param.type) {
      case 'slider':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">{param.min}{param.unit}</span>
              <span className="font-medium">{value}{param.unit}</span>
              <span className="text-slate-500">{param.max}{param.unit}</span>
            </div>
            <Slider
              value={[value]}
              min={param.min}
              max={param.max}
              step={param.step}
              onValueChange={([v]) => onChange(v)}
              className="w-full"
            />
          </div>
        );
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            min={param.min}
            max={param.max}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="h-8"
          />
        );
      case 'boolean':
        return (
          <div className="flex items-center gap-2">
            <Switch checked={value} onCheckedChange={onChange} />
            <span className="text-xs text-slate-500">{value ? 'Yes' : 'No'}</span>
          </div>
        );
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="h-8"
          />
        );
    }
  };
  
  // No schedule selected
  if (!activeSchedule) {
    return (
      <div className="flex flex-col items-center justify-center h-64" data-testid="scenarios-page">
        <GitBranch className="h-12 w-12 text-slate-300 mb-4" />
        <h2 className="text-lg font-medium text-slate-900 mb-2">No Schedule Selected</h2>
        <p className="text-sm text-slate-500">Select a project and schedule to manage scenarios</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scenarios-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            What-If Scenarios
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Create and analyze schedule scenarios for {activeSchedule.name}
          </p>
        </div>
        
        {can(Action.MANAGE_SCHEDULES) && (
          <Button
            className="h-9 bg-amber-500 hover:bg-amber-600 text-white font-medium gap-2"
            onClick={() => setCreateDialogOpen(true)}
            data-testid="create-scenario-button"
          >
            <Plus className="h-4 w-4" />
            Create Scenario
          </Button>
        )}
      </div>

      {/* Scenario Mode Warning */}
      {selectedScenarioId && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Scenario Mode</strong> – Changes here do not affect the live schedule. 
            This is a simulation for forecasting purposes only.
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
          <Button variant="ghost" size="sm" className="ml-auto h-6" onClick={() => setError(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {/* Template Gallery */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="h-4 w-4 text-amber-500" />
            Scenario Templates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTemplates ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {templates.slice(0, 5).map((template) => {
                const Icon = getTemplateIcon(template.icon);
                const style = getCategoryStyle(template.category);
                
                return (
                  <div
                    key={template.id}
                    className={`p-3 rounded-lg border ${style.border} ${style.bg} cursor-pointer hover:shadow-sm transition-all`}
                    onClick={() => {
                      setSelectedTemplateId(template.id);
                      setCreateMode('template');
                      setCreateDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`h-4 w-4 ${style.text}`} />
                      <Badge variant="outline" className={`text-[9px] ${style.text} ${style.border}`}>
                        {template.category}
                      </Badge>
                    </div>
                    <p className="text-xs font-medium text-slate-900">{template.name}</p>
                    <p className="text-[10px] text-slate-500 mt-1 line-clamp-2">{template.description}</p>
                  </div>
                );
              })}
              {templates.length > 5 && (
                <div
                  className="p-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-all flex flex-col items-center justify-center"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <Plus className="h-5 w-5 text-slate-400 mb-1" />
                  <p className="text-xs text-slate-500">View All</p>
                  <p className="text-[10px] text-slate-400">{templates.length - 5} more</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scenario List */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-900">
                My Scenarios
              </CardTitle>
              <Badge variant="outline" className="text-[10px]">
                {scenarios.length}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : scenarios.length > 0 ? (
              <div className="space-y-2">
                {scenarios.map((scenario) => {
                  const style = scenario.category ? getCategoryStyle(scenario.category) : getCategoryStyle('custom');
                  
                  return (
                    <div 
                      key={scenario.id} 
                      className={`p-3 rounded-lg border transition-colors cursor-pointer
                        ${selectedScenarioId === scenario.id 
                          ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-200' 
                          : 'border-slate-100 bg-slate-50/50 hover:bg-slate-50'}`}
                      onClick={() => loadScenarioDetails(scenario.id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-slate-900">{scenario.name}</span>
                        {getStatusBadge(scenario.status)}
                      </div>
                      {scenario.template_name && (
                        <div className="mb-2">
                          <Badge variant="outline" className={`text-[10px] ${style.text} ${style.border}`}>
                            {scenario.template_name}
                          </Badge>
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(scenario.created_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Layers className="h-3 w-3" />
                          {scenario.activity_count}
                        </div>
                      </div>
                      {scenario.variance_vs_current_days !== null && (
                        <div className="mt-2">
                          {getVarianceBadge(scenario.variance_vs_current_days)}
                        </div>
                      )}
                      {can(Action.MANAGE_SCHEDULES) && scenario.status === 'draft' && (
                        <div className="mt-2 flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScenario(scenario.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <GitBranch className="h-8 w-8 text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No scenarios created</p>
                <p className="text-xs text-slate-400 mt-1">Create a what-if scenario to explore</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Forecast Summary & Actions */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-900">
                Scenario Details
              </CardTitle>
              {selectedScenario && can(Action.MANAGE_SCHEDULES) && selectedScenario.is_editable && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={() => {
                      setDuplicateName(`${selectedScenario.name} (Copy)`);
                      setShowDuplicateDialog(true);
                    }}
                  >
                    <Copy className="h-3 w-3" />
                    Duplicate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1 text-purple-600 border-purple-200"
                    onClick={() => {
                      setBaselineName(`${selectedScenario.name} Baseline`);
                      setShowConvertDialog(true);
                    }}
                  >
                    <Flag className="h-3 w-3" />
                    Convert to Baseline
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={() => setShowCompareDialog(true)}
                  >
                    <BarChart3 className="h-3 w-3" />
                    Compare
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : forecast ? (
              <div className="space-y-4">
                {/* Key Dates */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-[10px] font-medium text-slate-500 uppercase">Baseline Finish</p>
                    <p className="text-sm font-medium text-slate-900 mt-1">
                      {formatDate(forecast.baseline_finish_date)}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-[10px] font-medium text-slate-500 uppercase">Current Plan</p>
                    <p className="text-sm font-medium text-slate-900 mt-1">
                      {formatDate(forecast.current_plan_finish_date)}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-[10px] font-medium text-amber-600 uppercase">Forecast Finish</p>
                    <p className="text-sm font-semibold text-amber-700 mt-1">
                      {formatDate(forecast.forecast_finish_date)}
                    </p>
                  </div>
                </div>
                
                {/* Variance Indicators */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-600">vs Current Plan</span>
                    </div>
                    {getVarianceBadge(forecast.variance_vs_current_days)}
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-600">vs Baseline</span>
                    </div>
                    {getVarianceBadge(forecast.variance_vs_baseline_days)}
                  </div>
                </div>
                
                {/* Template Parameters (if applicable) */}
                {selectedScenario?.template_id && selectedScenario?.parameters && Object.keys(selectedScenario.parameters).length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-[10px] font-medium text-slate-500 uppercase mb-2">Applied Parameters</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedScenario.parameters).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between text-xs">
                          <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="font-medium">
                            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Stats */}
                <div className="flex items-center gap-6 text-xs text-slate-500 pt-2 border-t border-slate-100">
                  <span>{forecast.total_activities} activities</span>
                  <span>{forecast.critical_activities} critical</span>
                  <span>{forecast.activities_changed} modified</span>
                  <span>{forecast.relationships_changed} relationships changed</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <Info className="h-8 w-8 text-slate-300 mb-2" />
                <p className="text-sm">Select a scenario to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Scenario Activities Table */}
      {selectedScenarioId && scenarioActivities.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900">
              Scenario Activities
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Activity</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Duration</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Forecast Start</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Forecast Finish</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Float</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scenarioActivities.slice(0, 20).map((act) => (
                  <TableRow key={act.id} className={`hover:bg-slate-50/50 ${act.is_modified ? 'bg-amber-50/30' : ''}`}>
                    <TableCell>
                      <div>
                        <span className="font-mono text-xs text-slate-500">{act.activity_id}</span>
                        <span className="ml-2 text-sm text-slate-900">{act.name}</span>
                        {act.is_critical && <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]">Critical</Badge>}
                        {act.is_modified && <Badge className="ml-1 bg-blue-100 text-blue-700 text-[10px]">Modified</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{act.original_duration}d</span>
                      {act.duration_change !== 0 && (
                        <span className={`ml-1 text-xs ${act.duration_change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ({act.duration_change > 0 ? '+' : ''}{act.duration_change})
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(act.forecast_start)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(act.forecast_finish)}</TableCell>
                    <TableCell>
                      <span className={`text-sm ${act.total_float === 0 ? 'text-amber-600 font-medium' : ''}`}>
                        {act.total_float ?? '—'}d
                      </span>
                    </TableCell>
                    <TableCell>
                      {act.finish_variance_days !== null ? (
                        <span className={`text-sm ${
                          act.finish_variance_days > 0 ? 'text-red-600' :
                          act.finish_variance_days < 0 ? 'text-green-600' : ''
                        }`}>
                          {act.finish_variance_days > 0 ? '+' : ''}{act.finish_variance_days}d
                        </span>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            {scenarioActivities.length > 20 && (
              <div className="p-3 text-center text-xs text-slate-500 border-t">
                Showing 20 of {scenarioActivities.length} activities
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Scenario Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-amber-500" />
              Create Scenario
            </DialogTitle>
            <DialogDescription>
              Create a what-if scenario to explore schedule changes safely
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0">
            <Tabs value={createMode} onValueChange={setCreateMode} className="w-full">
              <TabsList className="w-full grid grid-cols-2 mb-4">
                <TabsTrigger value="template">From Template</TabsTrigger>
                <TabsTrigger value="custom">Custom Scenario</TabsTrigger>
              </TabsList>
              
              <TabsContent value="template" className="space-y-4">
                {/* Template Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Select Template</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-1">
                    {templates.map((template) => {
                      const Icon = getTemplateIcon(template.icon);
                      const style = getCategoryStyle(template.category);
                      const isSelected = selectedTemplateId === template.id;
                      
                      return (
                        <div
                          key={template.id}
                          className={`p-2 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? `${style.bg} ${style.border} ring-1 ring-amber-400` 
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setSelectedTemplateId(template.id)}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${style.text}`} />
                            <span className="text-xs font-medium truncate">{template.name}</span>
                            {isSelected && <CheckCircle2 className="h-3 w-3 text-amber-500 ml-auto" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Parameters */}
                {selectedTemplateId && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Parameters</Label>
                    {templates.find(t => t.id === selectedTemplateId)?.parameters.map((param) => (
                      <div key={param.key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-slate-600">{param.label}</Label>
                          {param.unit && (
                            <span className="text-[10px] text-slate-400">{param.unit}</span>
                          )}
                        </div>
                        {renderParameterInput(
                          param,
                          templateParameters[param.key],
                          (value) => setTemplateParameters(prev => ({ ...prev, [param.key]: value }))
                        )}
                        {param.description && (
                          <p className="text-[10px] text-slate-400">{param.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="custom" className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Zap className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Create a blank scenario</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Edit activities and relationships manually
                  </p>
                </div>
              </TabsContent>
            </Tabs>
            
            <Separator className="my-4" />
            
            {/* Scenario Name & Description */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="scenario-name" className="text-sm font-medium">
                  Scenario Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="scenario-name"
                  value={newScenario.name}
                  onChange={(e) => setNewScenario(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Accelerated Schedule"
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="scenario-desc" className="text-sm font-medium">Description</Label>
                <Textarea
                  id="scenario-desc"
                  value={newScenario.description}
                  onChange={(e) => setNewScenario(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the scenario..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 mt-4">
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetCreateForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={createMode === 'template' ? handleCreateFromTemplate : handleCreateCustom}
              disabled={creating || !newScenario.name.trim() || (createMode === 'template' && !selectedTemplateId)}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Scenario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Duplicate Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-slate-500" />
              Duplicate Scenario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1">
              <Label htmlFor="dup-name">New Scenario Name</Label>
              <Input
                id="dup-name"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Enter name for the copy"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>Cancel</Button>
            <Button onClick={handleDuplicate} disabled={creating || !duplicateName.trim()}>
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Convert to Baseline Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-purple-500" />
              Convert to Baseline
            </DialogTitle>
            <DialogDescription>
              Create a baseline snapshot from this scenario
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1">
              <Label htmlFor="bl-name">Baseline Name</Label>
              <Input
                id="bl-name"
                value={baselineName}
                onChange={(e) => setBaselineName(e.target.value)}
                placeholder="Enter baseline name"
              />
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              The scenario will be marked as "Applied" after conversion.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleConvertToBaseline} 
              disabled={creating || !baselineName.trim()}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Convert to Baseline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Compare Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Compare Scenario with Baseline
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Baseline to Compare</Label>
              <Select value={selectedBaselineId || ''} onValueChange={setSelectedBaselineId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a baseline..." />
                </SelectTrigger>
                <SelectContent>
                  {baselines.map((bl) => (
                    <SelectItem key={bl.id} value={bl.id}>
                      <div className="flex items-center gap-2">
                        <Flag className="h-3 w-3 text-purple-500" />
                        {bl.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedBaselineId && (
              <Button onClick={handleCompare} disabled={loadingComparison} className="w-full">
                {loadingComparison ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Compare
              </Button>
            )}
            
            {comparisonData && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-[10px] text-amber-600 uppercase">Scenario Finish</p>
                    <p className="text-sm font-medium">{formatDate(comparisonData.scenario_finish_date)}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <p className="text-[10px] text-purple-600 uppercase">Baseline Finish</p>
                    <p className="text-sm font-medium">{formatDate(comparisonData.baseline_finish_date)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-[10px] text-slate-500 uppercase">Variance</p>
                    <p className={`text-sm font-medium ${
                      comparisonData.variance_days > 0 ? 'text-red-600' : 
                      comparisonData.variance_days < 0 ? 'text-green-600' : ''
                    }`}>
                      {comparisonData.variance_days > 0 ? '+' : ''}{comparisonData.variance_days || 0} days
                    </p>
                  </div>
                </div>
                
                <div className="text-xs text-slate-500">
                  {comparisonData.activities_with_changes} of {comparisonData.total_activities} activities changed
                  {comparisonData.critical_path_changed && (
                    <Badge className="ml-2 bg-amber-100 text-amber-700">Critical Path Changed</Badge>
                  )}
                </div>
                
                {comparisonData.activities?.length > 0 && (
                  <div className="max-h-[200px] overflow-y-auto overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">Activity</TableHead>
                          <TableHead className="text-xs">Duration Δ</TableHead>
                          <TableHead className="text-xs">Finish Δ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {comparisonData.activities.filter(a => a.duration_variance !== 0 || a.finish_variance_days !== 0).slice(0, 10).map((act) => (
                          <TableRow key={act.activity_id}>
                            <TableCell className="text-xs">{act.name}</TableCell>
                            <TableCell className={`text-xs ${act.duration_variance > 0 ? 'text-red-600' : act.duration_variance < 0 ? 'text-green-600' : ''}`}>
                              {act.duration_variance > 0 ? '+' : ''}{act.duration_variance}d
                            </TableCell>
                            <TableCell className={`text-xs ${act.finish_variance_days > 0 ? 'text-red-600' : act.finish_variance_days < 0 ? 'text-green-600' : ''}`}>
                              {act.finish_variance_days > 0 ? '+' : ''}{act.finish_variance_days || 0}d
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => { setShowCompareDialog(false); setComparisonData(null); setSelectedBaselineId(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScenariosPage;
